import { useState, useMemo, useRef } from "react";
import {
  Box,
  Drawer as MuiDrawer,
  IconButton,
  Typography,
  TextField,
  Stack,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Close as CloseIcon,
  History as HistoryIcon,
  Add as AddIcon,
  Send as SendIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  AutoAwesome as AutoAwesomeIcon,
  Analytics as AnalyticsIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  AccountTree as AccountTreeIcon,
} from "@mui/icons-material";
import { ChatMessage } from "./ChatMessage";

interface RichContent {
  type: "text" | "chart" | "table" | "list";
  content?: string;
  chart?: {
    type: "bar" | "line" | "pie";
    title?: string;
    data: unknown[];
    xKey: string;
    yKeys?: { key: string; name: string; color?: string }[];
    yKey?: string;
    pieDataKey?: string;
    pieNameKey?: string;
    colors?: string[];
  };
  table?: {
    headers: string[];
    rows: (string | number)[][];
  };
  list?: string[];
}

interface ChatMessageData {
  id: string;
  content: string | RichContent[];
  role: "user" | "assistant";
  timestamp: Date;
}

interface ChatHistory {
  id: string;
  title: string;
  messages: ChatMessageData[];
  updatedAt: Date;
}

export const AiChatDrawer = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [historyAnchorEl, setHistoryAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredChatId, setHoveredChatId] = useState<string | null>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const [chatHistories, setChatHistories] = useState<ChatHistory[]>([
    {
      id: "1",
      title: "Project Planning Discussion",
      messages: [
        {
          id: "1-1",
          role: "user",
          content:
            "Can you help me analyze the project timeline and identify potential bottlenecks?",
          timestamp: new Date(Date.now() - 3600000),
        },
        {
          id: "1-2",
          role: "assistant",
          content: [
            {
              type: "text",
              content:
                "I've analyzed your project timeline. Here's what I found:\n\nKey milestones are well-distributed, but there are a few areas that need attention:",
            },
            {
              type: "list",
              list: [
                "Phase 2 completion is scheduled for Q2, but resource allocation shows a 15% gap",
                "Critical path dependencies between Design and Construction phases need review",
                "Stakeholder approval process adds 3 weeks buffer that could be optimized",
              ],
            },
            {
              type: "chart",
              chart: {
                type: "line",
                title: "Project Timeline - Milestone Progress",
                data: [
                  { month: "Jan", planned: 0, actual: 0 },
                  { month: "Feb", planned: 15, actual: 12 },
                  { month: "Mar", planned: 30, actual: 28 },
                  { month: "Apr", planned: 45, actual: 42 },
                  { month: "May", planned: 60, actual: 58 },
                  { month: "Jun", planned: 75, actual: 72 },
                  { month: "Jul", planned: 90, actual: 85 },
                  { month: "Aug", planned: 100, actual: 95 },
                ],
                xKey: "month",
                yKeys: [
                  { key: "planned", name: "Planned %", color: "#1976d2" },
                  { key: "actual", name: "Actual %", color: "#2e7d32" },
                ],
              },
            },
            {
              type: "text",
              content:
                "\nRecommendations:\n• Consider accelerating Phase 1 deliverables by 1 week\n• Review resource allocation for Phase 2 to address the gap\n• Schedule stakeholder meetings earlier to reduce approval delays",
            },
          ],
          timestamp: new Date(Date.now() - 3500000),
        },
      ],
      updatedAt: new Date(Date.now() - 3500000),
    },
    {
      id: "2",
      title: "Budget Analysis",
      messages: [
        {
          id: "2-1",
          role: "user",
          content:
            "Show me a breakdown of the budget variance across different project phases.",
          timestamp: new Date(Date.now() - 7200000),
        },
        {
          id: "2-2",
          role: "assistant",
          content: [
            {
              type: "text",
              content:
                "Here's a comprehensive budget analysis across all project phases:",
            },
            {
              type: "table",
              table: {
                headers: [
                  "Phase",
                  "Budgeted (£)",
                  "Actual (£)",
                  "Variance (£)",
                  "Variance %",
                  "Status",
                ],
                rows: [
                  [
                    "Design",
                    "2,500,000",
                    "2,450,000",
                    "-50,000",
                    "-2.0%",
                    "Under Budget",
                  ],
                  [
                    "Construction",
                    "8,500,000",
                    "8,750,000",
                    "+250,000",
                    "+2.9%",
                    "Over Budget",
                  ],
                  [
                    "Testing",
                    "1,200,000",
                    "1,180,000",
                    "-20,000",
                    "-1.7%",
                    "Under Budget",
                  ],
                  [
                    "Deployment",
                    "1,800,000",
                    "1,950,000",
                    "+150,000",
                    "+8.3%",
                    "Over Budget",
                  ],
                  [
                    "Total",
                    "14,000,000",
                    "14,330,000",
                    "+330,000",
                    "+2.4%",
                    "Over Budget",
                  ],
                ],
              },
            },
            {
              type: "chart",
              chart: {
                type: "bar",
                title: "Budget vs Actual by Phase",
                data: [
                  { phase: "Design", budgeted: 2500, actual: 2450 },
                  { phase: "Construction", budgeted: 8500, actual: 8750 },
                  { phase: "Testing", budgeted: 1200, actual: 1180 },
                  { phase: "Deployment", budgeted: 1800, actual: 1950 },
                ],
                xKey: "phase",
                yKeys: [
                  { key: "budgeted", name: "Budgeted (£K)", color: "#1976d2" },
                  { key: "actual", name: "Actual (£K)", color: "#2e7d32" },
                ],
              },
            },
            {
              type: "text",
              content:
                "Key Insights:\n• Construction phase is 2.9% over budget due to material cost increases\n• Deployment phase shows 8.3% variance - needs immediate attention\n• Design and Testing phases are under budget, providing some buffer\n• Overall project is 2.4% over budget, within acceptable tolerance",
            },
          ],
          timestamp: new Date(Date.now() - 7100000),
        },
      ],
      updatedAt: new Date(Date.now() - 7100000),
    },
    {
      id: "3",
      title: "Risk Assessment Review",
      messages: [
        {
          id: "3-1",
          role: "user",
          content: "What are the top risks we should be concerned about?",
          timestamp: new Date(Date.now() - 10800000),
        },
        {
          id: "3-2",
          role: "assistant",
          content: [
            {
              type: "text",
              content:
                "Based on the current project status, here are the top risks ranked by impact and probability:",
            },
            {
              type: "chart",
              chart: {
                type: "pie",
                title: "Risk Distribution by Category",
                data: [
                  { name: "Schedule", value: 35 },
                  { name: "Budget", value: 25 },
                  { name: "Technical", value: 20 },
                  { name: "Resource", value: 15 },
                  { name: "External", value: 5 },
                ],
                xKey: "name",
                pieDataKey: "value",
                pieNameKey: "name",
              },
            },
            {
              type: "table",
              table: {
                headers: [
                  "Risk",
                  "Category",
                  "Probability",
                  "Impact",
                  "Risk Score",
                  "Mitigation",
                ],
                rows: [
                  [
                    "Delayed Material Delivery",
                    "Schedule",
                    "High",
                    "High",
                    "9",
                    "Diversify suppliers",
                  ],
                  [
                    "Budget Overrun",
                    "Budget",
                    "Medium",
                    "High",
                    "6",
                    "Weekly cost reviews",
                  ],
                  [
                    "Technical Complexity",
                    "Technical",
                    "Medium",
                    "Medium",
                    "4",
                    "POC validation",
                  ],
                  [
                    "Resource Availability",
                    "Resource",
                    "Low",
                    "High",
                    "3",
                    "Cross-training",
                  ],
                ],
              },
            },
            {
              type: "text",
              content:
                "Priority Actions:\n1. Establish backup suppliers for critical materials\n2. Implement weekly budget variance reviews\n3. Complete technical POC by end of month\n4. Cross-train team members on critical skills",
            },
          ],
          timestamp: new Date(Date.now() - 10700000),
        },
      ],
      updatedAt: new Date(Date.now() - 10700000),
    },
    {
      id: "4",
      title: "Schedule Planning",
      messages: [
        {
          id: "4-1",
          role: "user",
          content: "Can you help optimize the project schedule?",
          timestamp: new Date(Date.now() - 14400000),
        },
        {
          id: "4-2",
          role: "assistant",
          content: [
            {
              type: "text",
              content:
                "I've analyzed your schedule and identified optimization opportunities:",
            },
            {
              type: "chart",
              chart: {
                type: "line",
                title: "Schedule Performance Trend",
                data: [
                  { week: "W1", baseline: 0, current: 0 },
                  { week: "W2", baseline: 5, current: 4 },
                  { week: "W3", baseline: 10, current: 9 },
                  { week: "W4", baseline: 15, current: 14 },
                  { week: "W5", baseline: 20, current: 18 },
                  { week: "W6", baseline: 25, current: 22 },
                  { week: "W7", baseline: 30, current: 27 },
                  { week: "W8", baseline: 35, current: 31 },
                ],
                xKey: "week",
                yKeys: [
                  { key: "baseline", name: "Baseline %", color: "#1976d2" },
                  { key: "current", name: "Current %", color: "#ed6c02" },
                ],
              },
            },
            {
              type: "text",
              content:
                "Optimization Recommendations:\n\n1. Parallel Processing: Design and Procurement can run concurrently, saving 2 weeks\n2. Fast-Track Approval: Reduce stakeholder review cycles from 2 weeks to 1 week\n3. Resource Reallocation: Shift 2 engineers from Phase 2 to Phase 1 to accelerate delivery\n4. Critical Path: Focus on Design → Construction handoff to reduce delays",
            },
          ],
          timestamp: new Date(Date.now() - 14300000),
        },
      ],
      updatedAt: new Date(Date.now() - 14300000),
    },
  ]);

  const filteredChatHistories = useMemo(() => {
    if (!searchQuery.trim()) {
      return chatHistories;
    }
    const query = searchQuery.toLowerCase();
    return chatHistories.filter((chat) =>
      chat.title.toLowerCase().includes(query)
    );
  }, [chatHistories, searchQuery]);

  const handleHistoryClick = (event: React.MouseEvent<HTMLElement>) => {
    setHistoryAnchorEl(event.currentTarget);
  };

  const handleHistoryClose = () => {
    setHistoryAnchorEl(null);
    setSearchQuery("");
  };

  const handleNewChat = () => {
    setCurrentChatId(null);
    setMessage("");
    handleHistoryClose();
  };

  const handleSelectChat = (chatId: string) => {
    setCurrentChatId(chatId);
    handleHistoryClose();
  };

  const handleDeleteChat = (chatId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setChatHistories((prev) => prev.filter((chat) => chat.id !== chatId));
    if (currentChatId === chatId) {
      setCurrentChatId(null);
    }
  };

  const handleSendMessage = () => {
    if (!message.trim()) return;
    setMessage("");
  };

  const handlePredefinedQuestion = (question: string) => {
    setMessage(question);
    setTimeout(() => {
      messageInputRef.current?.focus();
    }, 100);
  };

  const isHistoryOpen = Boolean(historyAnchorEl);
  const theme = useTheme();

  const predefinedQuestions = [
    {
      category: "Budget Analysis",
      icon: <AnalyticsIcon />,
      questions: [
        "Show me a breakdown of the budget variance across different project phases",
        "What are the cost trends for the current quarter?",
        "Identify areas where we're over budget",
      ],
      color: theme.palette.primary.main,
    },
    {
      category: "Schedule Planning",
      icon: <ScheduleIcon />,
      questions: [
        "Can you help optimize the project schedule?",
        "What are the critical path dependencies?",
        "Show me potential schedule risks",
      ],
      color: theme.palette.info.main,
    },
    {
      category: "Risk Assessment",
      icon: <AssessmentIcon />,
      questions: [
        "What are the top risks we should be concerned about?",
        "Analyze risk mitigation strategies",
        "Show risk distribution by category",
      ],
      color: theme.palette.warning.main,
    },
    {
      category: "Performance",
      icon: <TrendingUpIcon />,
      questions: [
        "Analyze project performance metrics",
        "Compare planned vs actual progress",
        "What are the key performance indicators?",
      ],
      color: theme.palette.success.main,
    },
    {
      category: "Project Planning",
      icon: <AccountTreeIcon />,
      questions: [
        "Help me analyze the project timeline and identify bottlenecks",
        "What are the resource allocation gaps?",
        "Review project milestones and deliverables",
      ],
      color: theme.palette.secondary.main,
    },
  ];

  return (
    <MuiDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: "600px", md: "700px" },
          boxSizing: "border-box",
        },
      }}
    >
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "action.hover",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoAwesomeIcon
              sx={{
                fontSize: 20,
                color: theme.palette.primary.main,
              }}
            />
            <Typography variant="subtitle1" fontWeight={600}>
              {currentChatId
                ? chatHistories.find((chat) => chat.id === currentChatId)
                    ?.title || "Ask CapAI"
                : "Ask CapAI"}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={handleNewChat} size="small" color="primary">
              <AddIcon />
            </IconButton>
            <IconButton
              onClick={handleHistoryClick}
              size="small"
              color={isHistoryOpen ? "primary" : "default"}
            >
              <HistoryIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {currentChatId ? (
              (() => {
                const currentChat = chatHistories.find(
                  (chat) => chat.id === currentChatId
                );
                return currentChat ? (
                  <Box>
                    {currentChat.messages.map((msg) => (
                      <ChatMessage
                        key={msg.id}
                        role={msg.role}
                        content={msg.content}
                        timestamp={msg.timestamp}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Chat not found
                  </Typography>
                );
              })()
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  p: { xs: 2, sm: 3, md: 4 },
                  overflowY: "auto",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    mb: 3,
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <AutoAwesomeIcon
                      sx={{
                        fontSize: 40,
                        color: theme.palette.primary.main,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h5"
                    fontWeight={600}
                    sx={{ mb: 1 }}
                    color="text.primary"
                  >
                    How can I help you today?
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: 500, px: 2 }}
                  >
                    Ask me anything about your projects, budgets, schedules, or
                    risks. Or choose from the suggested questions below to get
                    started.
                  </Typography>
                </Box>

                <Box sx={{ width: "100%" }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{
                      mb: 2,
                      color: "text.secondary",
                      px: 2,
                    }}
                  >
                    Suggested Questions
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      overflowX: "auto",
                      overflowY: "hidden",
                      pb: 2,
                      px: 2,
                      scrollbarWidth: "thin",
                      scrollbarColor: `${theme.palette.divider} transparent`,
                      "&::-webkit-scrollbar": {
                        height: 8,
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "transparent",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: theme.palette.divider,
                        borderRadius: 4,
                        "&:hover": {
                          backgroundColor: theme.palette.action.disabled,
                        },
                      },
                    }}
                  >
                    {predefinedQuestions.map((category, categoryIndex) => (
                      <Card
                        key={categoryIndex}
                        variant="outlined"
                        sx={{
                          minWidth: 280,
                          maxWidth: 320,
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          borderColor: "divider",
                          flexShrink: 0,
                          "&:hover": {
                            borderColor: category.color,
                            boxShadow: `0 4px 12px ${alpha(
                              category.color,
                              0.15
                            )}`,
                          },
                        }}
                      >
                        <CardContent
                          sx={{ flex: 1, p: 2, "&:last-child": { pb: 2 } }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                color: category.color,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {category.icon}
                            </Box>
                            <Typography
                              variant="subtitle2"
                              fontWeight={600}
                              color="text.primary"
                            >
                              {category.category}
                            </Typography>
                          </Box>
                          <Stack spacing={1}>
                            {category.questions.map((question, qIndex) => (
                              <Card
                                key={qIndex}
                                variant="outlined"
                                sx={{
                                  p: 1.25,
                                  cursor: "pointer",
                                  borderColor: "divider",
                                  bgcolor: "background.default",
                                  "&:hover": {
                                    borderColor: category.color,
                                    bgcolor: alpha(category.color, 0.05),
                                  },
                                }}
                                onClick={() =>
                                  handlePredefinedQuestion(question)
                                }
                              >
                                <Typography
                                  variant="body2"
                                  color="text.primary"
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {question}
                                </Typography>
                              </Card>
                            ))}
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: "divider",
              bgcolor: "background.default",
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.background.paper, 0.8)
                    : theme.palette.background.paper,
                "&:focus-within": {
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 0 0 2px ${alpha(
                    theme.palette.primary.main,
                    0.1
                  )}`,
                },
              }}
            >
              <TextField
                inputRef={messageInputRef}
                fullWidth
                placeholder="Type your message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                multiline
                maxRows={4}
                size="small"
                variant="standard"
                sx={{
                  "& .MuiInputBase-root": {
                    fontSize: "0.9375rem",
                    "&::before": {
                      display: "none",
                    },
                    "&::after": {
                      display: "none",
                    },
                  },
                  "& .MuiInputBase-input": {
                    py: 0.5,
                  },
                }}
              />
              <IconButton
                onClick={handleSendMessage}
                color="primary"
                disabled={!message.trim()}
                sx={{
                  alignSelf: "flex-end",
                  mb: 0.5,
                  bgcolor: message.trim()
                    ? theme.palette.primary.main
                    : "transparent",
                  color: message.trim()
                    ? theme.palette.primary.contrastText
                    : theme.palette.action.disabled,
                  "&:hover": {
                    bgcolor: message.trim()
                      ? theme.palette.primary.dark
                      : "transparent",
                  },
                  "&:disabled": {
                    bgcolor: "transparent",
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                <SendIcon />
              </IconButton>
            </Stack>
          </Box>
        </Box>

        <Popover
          open={isHistoryOpen}
          anchorEl={historyAnchorEl}
          onClose={handleHistoryClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          sx={{
            mt: 1,
          }}
        >
          <Box
            sx={{
              width: 350,
              maxHeight: 500,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Chat History
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Search chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <List
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 0,
                maxHeight: 400,
              }}
            >
              {filteredChatHistories.length > 0 ? (
                filteredChatHistories.map((chat) => (
                  <ListItem
                    key={chat.id}
                    disablePadding
                    onMouseEnter={() => setHoveredChatId(chat.id)}
                    onMouseLeave={() => setHoveredChatId(null)}
                    sx={{
                      position: "relative",
                    }}
                  >
                    <ListItemButton
                      selected={currentChatId === chat.id}
                      onClick={() => handleSelectChat(chat.id)}
                      sx={{
                        pr: 6,
                      }}
                    >
                      <ListItemText
                        primary={chat.title}
                        secondary={chat.updatedAt.toLocaleDateString()}
                      />
                    </ListItemButton>
                    {hoveredChatId === chat.id && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleDeleteChat(chat.id, e)}
                        sx={{
                          position: "absolute",
                          right: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "error.main",
                          "&:hover": {
                            bgcolor: "error.light",
                            color: "error.dark",
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText
                    primary="No chats found"
                    primaryTypographyProps={{
                      variant: "body2",
                      color: "text.secondary",
                    }}
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </Popover>
      </Box>
    </MuiDrawer>
  );
};
